import { useState, useEffect } from 'react';
import { checkSubscriptionStatus, getTrialDaysRemaining, isTrialEndingSoon, type SubscriptionInfo } from '../utils/subscription';

interface SubscriptionNotificationProps {
  userId: string;
  onUpgradeClick?: () => void;
  onRetryPaymentClick?: () => void;
}

export default function SubscriptionNotification({ userId, onRetryPaymentClick }: SubscriptionNotificationProps) {
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [notificationType, setNotificationType] = useState<'trial' | 'payment_failed' | 'past_due' | 'unpaid' | null>(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, [userId]);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const subscriptionInfo = await checkSubscriptionStatus(userId);
      setSubscription(subscriptionInfo);
      
      // Determine notification type and visibility
      if (subscriptionInfo) {
        const { plan, planStatus, subscriptionStatus, trialEndDate } = subscriptionInfo;
        
        // Check for trial ending soon
        if (plan === 'trial' && trialEndDate && isTrialEndingSoon(trialEndDate)) {
          setNotificationType('trial');
          setIsVisible(true);
        }
        // Check for payment failures
        else if (subscriptionStatus === 'past_due') {
          setNotificationType('past_due');
          setIsVisible(true);
        }
        else if (subscriptionStatus === 'unpaid') {
          setNotificationType('unpaid');
          setIsVisible(true);
        }
        else if (planStatus === 'past_due' || planStatus === 'unpaid') {
          setNotificationType('payment_failed');
          setIsVisible(true);
        }
        else {
          setIsVisible(false);
          setNotificationType(null);
        }
      } else {
        setIsVisible(false);
        setNotificationType(null);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      setIsVisible(false);
      setNotificationType(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (loading || !isVisible || !subscription || !notificationType) {
    return null;
  }

  const daysRemaining = subscription.trialEndDate ? getTrialDaysRemaining(subscription.trialEndDate) : 0;
  const isUrgent = daysRemaining <= 1;

  // Render different notification types
  const renderTrialNotification = () => (
    <div className={`relative bg-gradient-to-r ${
      isUrgent 
        ? 'from-red-500 to-red-600' 
        : 'from-orange-500 to-orange-600'
    } text-white shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <i className={`text-xl ${isUrgent ? 'ri-error-warning-line' : 'ri-time-line'}`}></i>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium">
                {isUrgent ? '¡Atención! Tu período de prueba termina hoy' : 'Tu período de prueba termina pronto'}
              </h3>
              <p className="text-xs opacity-90 mt-1">
                {isUrgent 
                  ? 'Tu período de prueba termina hoy. Asegúrate de tener fondos suficientes en tu cuenta para continuar con el servicio.'
                  : `Tu período de prueba termina en ${daysRemaining} día${daysRemaining !== 1 ? 's' : ''}. Asegúrate de tener fondos suficientes en tu cuenta para continuar con el servicio.`
                }
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black bg-opacity-20">
        <div 
          className={`h-full transition-all duration-300 ${
            isUrgent ? 'bg-red-300' : 'bg-orange-300'
          }`}
          style={{ 
            width: `${Math.max(0, Math.min(100, (daysRemaining / 4) * 100))}%` 
          }}
        ></div>
      </div>
    </div>
  );

  const renderPaymentFailedNotification = () => (
    <div className="relative bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <i className="ri-error-warning-line text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium">
                ¡Pago Fallido! Tu suscripción necesita atención
              </h3>
              <p className="text-xs opacity-90 mt-1">
                {notificationType === 'past_due' 
                  ? 'Tu pago está pendiente. Actualiza tu método de pago para evitar la suspensión del servicio.'
                  : 'No se pudo procesar tu pago. Verifica tu método de pago y fondos disponibles.'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onRetryPaymentClick}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-red-600 hover:bg-gray-100"
            >
              <i className="ri-refresh-line mr-1"></i>
              Reintentar Pago
            </button>
            
            <button
              onClick={handleDismiss}
              className="text-white hover:text-gray-200 transition-colors"
              title="Cerrar notificación"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
      
      {/* Urgent indicator */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-300"></div>
    </div>
  );

  // Return appropriate notification based on type
  switch (notificationType) {
    case 'trial':
      return renderTrialNotification();
    case 'past_due':
    case 'unpaid':
    case 'payment_failed':
      return renderPaymentFailedNotification();
    default:
      return null;
  }
}
